import { InstallationModuleStatus, InstallationState } from "./index";
import {
  InstallationModule,
  DeliveryDocumentModuleType,
  BreakerStatus,
  ModuleStatusContext
} from "@/types";
import { ActionTree } from "vuex/types";
import { AxiosError } from "axios";

function commandError(e: AxiosError): Error {
  if (e.response) {
    return new Error(`Niet gelukt (fout ${e.response.status}). Probeer het opnieuw.`);
  }

  if (e.isAxiosError) {
    return new Error("Niet gelukt: geen verbinding. Probeer het opnieuw.");
  }

  return new Error(`Niet gelukt: ${e.message}. Probeer het opnieuw.`);
}

export const actionsAsync: ActionTree<InstallationState, any> = {
  async ["poll:resume"](
    { dispatch, getters },
    { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
  ) {
    const status: InstallationModuleStatus = getters["status"](id, moduleType);

    window.clearTimeout(status.timeoutId);

    if (status.status === "processing") {
      switch (status.context) {
        case "connect":
        case "disconnect":
        case "production_test":
        case "switch_breaker":
          dispatch("poll", {
            id,
            type: moduleType,
            timeout: 0,
            context: status.context
          });
      }
    }

    return false;
  },

  async ["meter:connect"](
    { commit, getters, dispatch, rootGetters },
    { id, type }: { id: number; type: string }
  ) {
    const installationModule: InstallationModule = getters["module"](id, type);

    if (installationModule.flags?.hasAssociation) return false;

    this.dispatch(
      "track",
      `request register meter s/n: ${installationModule.data?.meterSerialNumbers?.[0].serialNumber} for installation: ${id}`
    );

    const meta = { id, type, context: "connect" };

    commit(`poll:pending`, { meta });

    try {
      await dispatch("save", { id, moduleType: type });

      const serialNumber = installationModule.data?.meterSerialNumbers?.[0]?.serialNumber;

      await this.$axios.$post(`/installations/${id}/associate-meter`, {
        serialNumber
      });
    } catch (e) {
      commit(`poll:rejected`, { payload: commandError(e), meta });
      throw e;
    }

    return dispatch("poll:resume", { id, moduleType: type });
  },

  async ["meter:disconnect"](
    { commit, dispatch, getters },
    { id, type }: { id: number; type: string }
  ) {
    const installationModule: InstallationModule = getters["module"](id, type);

    if (!installationModule.flags?.hasAssociation) return false;

    const meta = { id, type, context: "disconnect" };

    commit(`poll:pending`, { meta });

    try {
      await this.$axios.$delete(`/installations/${id}/associate-meter`);
    } catch (e) {
      commit(`poll:rejected`, { payload: commandError(e), meta });
      throw e;
    }

    return dispatch("poll:resume", { id, moduleType: type });
  },

  async ["meter:test"]({ commit, getters, dispatch }, { id, type }: { id: number; type: string }) {
    const installationModule: InstallationModule = getters["module"](id, type);

    if (installationModule.flags?.hasProductionTest) return false;

    this.dispatch(
      "track",
      `request production test for meter s/n: ${installationModule.data?.meterSerialNumbers?.[0].serialNumber} for installation: ${id}`
    );

    const meterId = installationModule.data?.meterId;
    const serialNumber = installationModule.data?.meterSerialNumbers?.[0]?.serialNumber;

    const meta = { id, type, context: "production_test" };

    commit(`poll:pending`, { meta });

    try {
      await this.$axios.$post(`/installations/${id}/production-test`, {
        meterId,
        serialNumber
      });
    } catch (e) {
      commit(`poll:rejected`, { payload: commandError(e), meta });
      throw e;
    }

    return dispatch("poll:resume", { id, moduleType: type });
  },

  async ["toggle:breaker"](
    { commit, getters, dispatch },
    { id, type }: { id: number; type: string }
  ) {
    const installationModule: InstallationModule = getters["module"](id, type);

    const serialNumber = installationModule.data?.meterSerialNumbers?.[0]?.serialNumber;

    this.dispatch(
      "track",
      `request toggle break for meter s/n: ${installationModule.data?.meterSerialNumbers?.[0].serialNumber} for installation: ${id}`
    );

    const meta = { id, type, context: "switch_breaker" as ModuleStatusContext };

    commit(`poll:pending`, { meta });

    try {
      return await this.$axios.$post(`/installations/${id}/switch-breaker`, {
        serialNumber
      });
    } catch (e) {
      commit(`poll:rejected`, { payload: commandError(e), meta });
      throw e;
    }
  },

  async ["meter:breakerStatus"](context, id: number) {
    return this.$axios.$get<BreakerStatus>(`/installations/${id}/breaker-status`);
  }
};
